import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import Cookies from 'js-cookie'
import { User } from '@/types'
import { authService } from '@/services/auth'
import { setAuthToken, clearAuth } from '@/services/api'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (credentials: { username: string; password: string; totp_code?: string }) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  clearError: () => void
  checkAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // Estado inicial
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Acciones
      login: async (credentials) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await authService.login(credentials)
          setAuthToken(response.access_token)
          
          // Obtener información del usuario
          const user = await authService.getCurrentUser()
          
          set({ 
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null 
          })
          
        } catch (error: any) {
          set({ 
            isLoading: false, 
            error: error.response?.data?.detail || 'Error de autenticación',
            isAuthenticated: false,
            user: null 
          })
          throw error
        }
      },

      logout: () => {
        clearAuth()
        set({
          user: null,
          isAuthenticated: false,
          error: null
        })
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true })
      },

      clearError: () => {
        set({ error: null })
      },

      checkAuth: async () => {
        const token = Cookies.get('access_token')
        if (!token) {
          set({ isAuthenticated: false, user: null, isLoading: false })
          return
        }

        try {
          set({ isLoading: true })
          const user = await authService.getCurrentUser()
          set({ 
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null
          })
        } catch (error) {
          console.error('Auth check failed:', error)
          clearAuth()
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null
          })
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ 
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      }),
    }
  )
)